import { useEffect, useMemo, useState } from 'react';
import { Title, useNotify, useTranslate } from 'react-admin';
import {
  Box,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material';
import * as echarts from 'echarts';

import { API_URL, httpClient } from '../../../httpClient';
import {
  CHART_COLORS,
  ChartCard,
  type ConversionDashboardData,
  DashboardHeader,
  DateRangeFilter,
  EChartsComponent,
  isoToDateInputValue,
  toBrowserDayIsoRange,
  useDefaultDateRange,
} from './shared';

/** Stage-by-stage funnel along the status ladder ("where is the gap"). */
interface FunnelByStage {
  statusId: number;
  statusName: string;
  order: number;
  reached: number;
  exited: number;
  conversionToNext: string;
}

type ConversionData = ConversionDashboardData & {
  funnelByStage?: FunnelByStage[];
};

export const ConversionDashboard = () => {
  const translate = useTranslate();
  const theme = useTheme();
  const notify = useNotify();
  const { ninetyDaysAgo, today } = useDefaultDateRange();

  const [startDate, setStartDate] = useState<string>(ninetyDaysAgo);
  const [endDate, setEndDate] = useState<string>(today);
  const [data, setData] = useState<ConversionData | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Guard against a slow earlier response overwriting a newer one when the
    // date range changes rapidly (no AbortController → stale-response check).
    let cancelled = false;
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const queryParams = new URLSearchParams();
        const startIso = toBrowserDayIsoRange(startDate, false);
        const endIso = toBrowserDayIsoRange(endDate, true);
        if (startIso) queryParams.append('startDate', startIso);
        if (endIso) queryParams.append('endDate', endIso);

        const { json } = (await httpClient(
          `${API_URL}/job-application/dashboard/conversion?${queryParams.toString()}`,
          { method: 'GET' },
        )) as { json: { data: ConversionData } };

        if (!cancelled) setData(json.data);
      } catch (error) {
        if (cancelled) return;
        console.error('Error fetching conversion dashboard:', error);
        notify('ra.notification.http_error', { type: 'error' });
        setData(null);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    fetchData();
    return () => {
      cancelled = true;
    };
  }, [startDate, endDate, notify]);

  const funnelOption = useMemo<echarts.EChartsOption>(() => {
    const points = data?.funnel ?? [];
    return {
      tooltip: {
        trigger: 'item',
        formatter: (params: unknown) => {
          const d = (params as { data: { name: string; value: number; conversionRate: string } }).data;
          return `${d.name}: <strong>${d.value}</strong><br/>${d.conversionRate}`;
        },
        backgroundColor: theme.palette.background.paper,
        borderColor: theme.palette.divider,
        textStyle: { color: theme.palette.text.primary },
      },
      series: [
        {
          type: 'funnel',
          left: '5%',
          top: 30,
          bottom: 30,
          width: '52%',
          min: 0,
          max: points.length > 0 ? points[0].value : 100,
          minSize: '14%',
          maxSize: '100%',
          sort: 'none',
          gap: 2,
          label: {
            show: true,
            position: 'right',
            formatter: (params: unknown) => {
              const d = (params as { data: { name: string; value: number; conversionRate: string } }).data;
              return `{name|${d.name}}\n{val|${d.value}}  {rate|${d.conversionRate}}`;
            },
            color: theme.palette.text.primary,
            fontSize: 12,
            lineHeight: 16,
            rich: {
              name: { color: theme.palette.text.primary, fontWeight: 'bold', fontSize: 12, lineHeight: 16 },
              val: { color: theme.palette.text.primary, fontSize: 12 },
              rate: { color: theme.palette.text.secondary, fontSize: 11 },
            },
          },
          labelLine: {
            show: true,
            length: 24,
            lineStyle: { color: theme.palette.divider, width: 1 },
          },
          emphasis: { label: { fontWeight: 'bold' } },
          itemStyle: { borderColor: theme.palette.background.paper, borderWidth: 2 },
          data: points.map((p, i) => ({
            value: p.value,
            name: p.name,
            conversionRate: p.conversionRate,
            itemStyle: { color: CHART_COLORS[i % CHART_COLORS.length] },
          })),
        },
      ],
    };
  }, [data, theme]);

  // Stage-by-stage ladder funnel: how many distinct apps reached each status
  // and how many exited there — the "where is the gap" view. Horizontal bars,
  // top-to-bottom along the ladder order.
  const funnelByStageOption = useMemo<echarts.EChartsOption>(() => {
    const stages = data?.funnelByStage ?? [];
    if (!stages.length) {
      return {
        graphic: {
          type: 'text',
          left: 'center',
          top: 'middle',
          style: {
            text: translate('resources.job-application-dashboard.charts.noData'),
            fontSize: 14,
            fill: theme.palette.text.secondary,
          },
        },
      };
    }

    // ECharts category axis draws bottom-up; reverse so the first ladder stage
    // sits at the top.
    const ordered = [...stages].reverse();
    const names = ordered.map((s) => s.statusName);

    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        formatter: (params: unknown) => {
          const arr = params as Array<{ dataIndex: number }>;
          const s = ordered[arr[0]?.dataIndex ?? 0];
          if (!s) return '';
          return [
            `<strong>${s.statusName}</strong>`,
            `${translate('resources.job-application-dashboard.dashboards.conversion.stageReached')}: ${s.reached}`,
            `${translate('resources.job-application-dashboard.dashboards.conversion.stageExited')}: ${s.exited}`,
            `${translate('resources.job-application-dashboard.dashboards.conversion.stageConversionToNext')}: ${s.conversionToNext}`,
          ].join('<br/>');
        },
        backgroundColor: theme.palette.background.paper,
        borderColor: theme.palette.divider,
        textStyle: { color: theme.palette.text.primary },
      },
      legend: {
        top: '2%',
        textStyle: { color: theme.palette.text.secondary },
      },
      grid: { left: '3%', right: '8%', bottom: '3%', top: '15%', containLabel: true },
      xAxis: {
        type: 'value',
        axisLabel: { color: theme.palette.text.secondary },
        splitLine: { lineStyle: { color: theme.palette.divider, type: 'dashed' } },
      },
      yAxis: {
        type: 'category',
        data: names,
        axisLabel: {
          color: theme.palette.text.secondary,
          fontSize: 11,
          width: 120,
          overflow: 'truncate',
          ellipsis: '...',
        },
        axisLine: { lineStyle: { color: theme.palette.divider } },
      },
      series: [
        {
          name: translate('resources.job-application-dashboard.dashboards.conversion.stageReached'),
          type: 'bar',
          data: ordered.map((s) => s.reached),
          itemStyle: { color: CHART_COLORS[0], borderRadius: [0, 4, 4, 0] },
          barWidth: '55%',
          label: {
            show: true,
            position: 'right',
            color: theme.palette.text.secondary,
            fontSize: 11,
            formatter: '{c}',
          },
        },
        {
          name: translate('resources.job-application-dashboard.dashboards.conversion.stageExited'),
          type: 'bar',
          data: ordered.map((s) => s.exited),
          itemStyle: { color: CHART_COLORS[3 % CHART_COLORS.length], borderRadius: [0, 4, 4, 0] },
          barWidth: '55%',
          label: {
            show: true,
            position: 'right',
            color: theme.palette.text.secondary,
            fontSize: 11,
            formatter: '{c}',
          },
        },
      ],
    };
  }, [data, theme, translate]);

  // Group closure reasons by category for grouped bar chart
  const closureReasonsOption = useMemo<echarts.EChartsOption>(() => {
    const reasons = data?.closureReasons ?? [];
    if (!reasons.length) {
      return {
        graphic: {
          type: 'text',
          left: 'center',
          top: 'middle',
          style: {
            text: translate('resources.job-application-dashboard.charts.noData'),
            fontSize: 14,
            fill: theme.palette.text.secondary,
          },
        },
      };
    }

    // Get all categories and reasons
    const categories = Array.from(new Set(reasons.map((r) => r.category)));
    const reasonNames = Array.from(new Set(reasons.map((r) => r.reasonName)));

    // `category` is the raw closure-category enum; map it to a friendly label
    // for the legend/tooltip (the match below still keys off the raw value).
    const labelForCategory = (enumValue: string): string =>
      translate(
        `resources.job-application-closure-reason.categoryChoices.${enumValue}`,
        { _: enumValue },
      );

    const series = categories.map((category, i) => ({
      name: labelForCategory(category),
      type: 'bar' as const,
      stack: 'total',
      itemStyle: {
        color: CHART_COLORS[i % CHART_COLORS.length],
        borderRadius: [0, 0, 0, 0],
      },
      data: reasonNames.map((reasonName) => {
        const match = reasons.find((r) => r.reasonName === reasonName && r.category === category);
        return match ? match.count : 0;
      }),
    }));

    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        backgroundColor: theme.palette.background.paper,
        borderColor: theme.palette.divider,
        textStyle: { color: theme.palette.text.primary },
      },
      legend: { top: '2%', textStyle: { color: theme.palette.text.secondary } },
      grid: { left: '3%', right: '4%', bottom: '3%', top: '15%', containLabel: true },
      xAxis: {
        type: 'category',
        data: reasonNames,
        axisLabel: {
          rotate: 30,
          color: theme.palette.text.secondary,
          fontSize: 11,
          width: 120,
          overflow: 'truncate',
        },
        axisLine: { lineStyle: { color: theme.palette.divider } },
      },
      yAxis: {
        type: 'value',
        axisLabel: { color: theme.palette.text.secondary },
        splitLine: { lineStyle: { color: theme.palette.divider, type: 'dashed' } },
      },
      series,
    };
  }, [data, theme, translate]);

  const exitedFromStatusOption = useMemo<echarts.EChartsOption>(() => {
    const points = data?.exitedFromStatus ?? [];
    const reversed = [...points].reverse();
    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        backgroundColor: theme.palette.background.paper,
        borderColor: theme.palette.divider,
        textStyle: { color: theme.palette.text.primary },
      },
      grid: { left: '3%', right: '8%', bottom: '3%', top: '3%', containLabel: true },
      xAxis: {
        type: 'value',
        axisLabel: { color: theme.palette.text.secondary },
        splitLine: { lineStyle: { color: theme.palette.divider, type: 'dashed' } },
      },
      yAxis: {
        type: 'category',
        data: reversed.map((p) => p.name),
        axisLabel: {
          color: theme.palette.text.secondary,
          fontSize: 11,
          width: 120,
          overflow: 'truncate',
          ellipsis: '...',
        },
        axisLine: { lineStyle: { color: theme.palette.divider } },
      },
      series: [
        {
          type: 'bar',
          data: reversed.map((p, i) => ({
            value: p.value,
            itemStyle: {
              color: CHART_COLORS[(points.length - 1 - i) % CHART_COLORS.length],
              borderRadius: [0, 4, 4, 0],
            },
          })),
          barWidth: '65%',
          label: {
            show: true,
            position: 'right',
            color: theme.palette.text.secondary,
            fontSize: 11,
            formatter: '{c}',
          },
        },
      ],
    };
  }, [data, theme]);

  const timings = data?.statusTimings ?? [];

  return (
    <Box sx={{ p: 3 }}>
      <Title title={translate('resources.job-application-dashboard.dashboards.tabConversion')} />
      <DashboardHeader
        title={translate('resources.job-application-dashboard.dashboards.tabConversion')}
        dataSinceAt={data?.dataSinceAt}
      >
        <DateRangeFilter
          startDate={startDate}
          endDate={endDate}
          onStartDateChange={setStartDate}
          onEndDateChange={setEndDate}
          minDate={isoToDateInputValue(data?.dataSinceAt)}
        />
      </DashboardHeader>

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, lg: 6 }}>
          <ChartCard
            title={translate('resources.job-application-dashboard.dashboards.conversion.funnel')}
            tooltip={translate('resources.job-application-dashboard.dashboards.conversion.tooltips.funnel')}
          >
            <EChartsComponent option={funnelOption} height={400} loading={isLoading} />
          </ChartCard>
        </Grid>
        <Grid size={{ xs: 12, lg: 6 }}>
          <ChartCard
            title={translate('resources.job-application-dashboard.dashboards.conversion.exitedFromStatus')}
            tooltip={translate('resources.job-application-dashboard.dashboards.conversion.tooltips.exitedFromStatus')}
          >
            <EChartsComponent option={exitedFromStatusOption} height={400} loading={isLoading} />
          </ChartCard>
        </Grid>
        <Grid size={{ xs: 12 }}>
          <ChartCard
            title={translate('resources.job-application-dashboard.dashboards.conversion.funnelByStage')}
            tooltip={translate('resources.job-application-dashboard.dashboards.conversion.tooltips.funnelByStage')}
          >
            <EChartsComponent option={funnelByStageOption} height={400} loading={isLoading} />
          </ChartCard>
        </Grid>
        <Grid size={{ xs: 12 }}>
          <ChartCard
            title={translate('resources.job-application-dashboard.dashboards.conversion.closureReasons')}
            tooltip={translate('resources.job-application-dashboard.dashboards.conversion.tooltips.closureReasons')}
          >
            <EChartsComponent option={closureReasonsOption} height={400} loading={isLoading} />
          </ChartCard>
        </Grid>
        <Grid size={{ xs: 12 }}>
          <ChartCard
            title={translate('resources.job-application-dashboard.dashboards.conversion.statusTimings')}
            tooltip={translate('resources.job-application-dashboard.dashboards.conversion.tooltips.statusTimings')}
          >
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {translate('resources.job-application-dashboard.dashboards.operations.statusName')}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      {translate('resources.job-application-dashboard.dashboards.conversion.timingAvg')}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      {translate('resources.job-application-dashboard.dashboards.conversion.timingMin')}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      {translate('resources.job-application-dashboard.dashboards.conversion.timingMax')}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      {translate('resources.job-application-dashboard.dashboards.conversion.timingTotal')}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      {translate('resources.job-application-dashboard.dashboards.conversion.timingCount')}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {timings.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                          {translate('resources.job-application-dashboard.charts.noData')}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    timings.map((row) => (
                      <TableRow key={row.statusId} hover>
                        <TableCell>{row.statusName}</TableCell>
                        <TableCell align="right">{row.averageDays.toFixed(1)}</TableCell>
                        <TableCell align="right">{row.minDays.toFixed(1)}</TableCell>
                        <TableCell align="right">{row.maxDays.toFixed(1)}</TableCell>
                        <TableCell align="right">{row.totalDays.toFixed(1)}</TableCell>
                        <TableCell align="right">{row.count}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </ChartCard>
        </Grid>
      </Grid>
    </Box>
  );
};
